import React, { useEffect, useRef, useState } from 'react';
import { useSparks } from '../../contexts/SparksContext';
import { formatSparks } from '../../api/sparksApi';
import audioManager from '../../utils/audioManager';
import { bracketFor, levelBadgeUrl, levelProgress, streakBadgeUrl } from './achievementRules';
import MasteredShield from './MasteredShield';
import '../StreakBadge.css';
import './achievements.css';

const FLY_MS = 700;
const UNLOCKED_ITEMS = { champion_headband: 'Champion Headband', streak_crown: 'Streak Crown' };

const plural = (n, one, many) => (n === 1 ? one : many);

/**
 * The celebration card queue (levels plan §7): one card at a time, each dismissed with Continue.
 * Cards only play at the end of an activity: they wait until the recap releases them after its
 * count-up (`cardsReleased`), and never while a question is on screen (the Addy burst is mounted).
 * Badge cards end by flying the badge to the Achievements link so the child sees where it went.
 */
export default function CelebrationQueue() {
  const sparks = useSparks();
  const { celebrations, burstMounted, cardsReleased, dismissCelebration } = sparks;
  const card = cardsReleased && !burstMounted ? celebrations[0] : null;
  const badgeRef = useRef(null);
  const [flight, setFlight] = useState(null);

  const cardId = card?.id;
  useEffect(() => {
    if (cardId) audioManager.play(process.env.PUBLIC_URL + '/streak-fanfare.mp3', 0.65);
  }, [cardId]);

  if (!card) return null;

  const onContinue = () => {
    const from = badgeRef.current?.getBoundingClientRect();
    const target = document.querySelector('[data-achievements-target]')?.getBoundingClientRect();
    if (!from || !target || !target.width) {
      dismissCelebration();
      return;
    }
    setFlight({
      src: badgeRef.current.src,
      left: from.left,
      top: from.top,
      size: from.width,
      dx: target.left + target.width / 2 - (from.left + from.width / 2),
      dy: target.top + target.height / 2 - (from.top + from.height / 2),
      scale: Math.max(0.15, target.height / from.height),
    });
    setTimeout(() => {
      setFlight(null);
      dismissCelebration();
    }, FLY_MS);
  };

  return (
    <div className="streak-overlay fixed inset-0 z-50 flex items-center justify-center bg-black/50" role="dialog" aria-modal="true">
      <div key={card.id} className="streak-celebration-card bg-[#5C3BD1] rounded-3xl px-12 py-9 text-center text-white shadow-2xl max-w-md max-md:mx-4 max-md:max-w-[calc(100vw-2rem)] max-md:px-6 max-md:py-8">
        <CardBody card={card} sparks={sparks} badgeRef={badgeRef} hidden={!!flight} />
        <button
          onClick={onContinue}
          disabled={!!flight}
          className="mt-6 px-8 py-2.5 bg-white text-[#5C3BD1] font-semibold font-poppins rounded-full text-base hover:bg-white/90 transition-colors duration-150"
        >
          Continue
        </button>
      </div>
      {flight && (
        <img
          src={flight.src}
          alt=""
          className="badge-flight"
          style={{ left: flight.left, top: flight.top, width: flight.size, '--dx': `${flight.dx}px`, '--dy': `${flight.dy}px`, '--scale': flight.scale, animationDuration: `${FLY_MS}ms` }}
        />
      )}
    </div>
  );
}

function CardBody({ card, sparks, badgeRef, hidden }) {
  const badgeStyle = hidden ? { visibility: 'hidden' } : undefined;
  if (card.type === 'streak-day') {
    return (
      <>
        <div className="text-7xl leading-none mb-3 max-md:text-5xl">🔥</div>
        <div className="text-8xl font-bold font-poppins leading-none max-md:text-6xl">{card.streak}</div>
        <div className="text-2xl font-semibold font-poppins mt-2 tracking-wide">Day Streak!</div>
        {card.amount > 0 && <div className="text-lg font-poppins mt-2">+{formatSparks(card.amount)} ✨</div>}
        <div className="text-sm font-poppins mt-3 tracking-widest uppercase text-white/70">Keep it up!</div>
      </>
    );
  }
  if (card.type === 'freeze-earned') {
    return (
      <>
        <div className="text-7xl leading-none mb-3 max-md:text-5xl">❄️</div>
        <div className="text-3xl font-bold font-poppins">Streak freeze earned!</div>
        <p className="font-poppins mt-3 text-white/90">
          A freeze protects your streak for a day you miss. Earn one every 3 days in a row, up to {sparks.freezeCap}.
        </p>
        <div className="text-sm font-poppins mt-3 tracking-widest uppercase text-white/70">Freezes: {card.freezes} / {sparks.freezeCap}</div>
      </>
    );
  }
  if (card.type === 'freeze-saved') {
    return (
      <>
        <div className="text-7xl leading-none mb-3 max-md:text-5xl">❄️</div>
        <div className="text-3xl font-bold font-poppins">Your streak is safe!</div>
        <p className="font-poppins mt-3 text-white/90">
          You missed {plural(card.freezes, 'a day', `${card.freezes} days`)}, but {plural(card.freezes, 'a freeze', 'your freezes')} kept your streak going.
        </p>
        <div className="text-sm font-poppins mt-3 tracking-widest uppercase text-white/70">Freezes left: {sparks.streakFreezes}</div>
      </>
    );
  }
  if (card.type === 'streak-badge') {
    const badge = sparks.streakBadges.find((b) => b.key === card.key);
    const item = UNLOCKED_ITEMS[badge?.itemKey];
    return (
      <>
        <div className="badge-burst mx-auto">
          <img ref={badgeRef} src={streakBadgeUrl(card.day)} alt="" width={160} height={168} style={badgeStyle} />
        </div>
        <div className="text-3xl font-bold font-poppins mt-2">{badge?.name || `${card.day}-day streak`}</div>
        <div className="font-poppins text-white/80">{card.day}-day streak</div>
        {card.amount > 0 && <div className="text-lg font-poppins mt-2">+{formatSparks(card.amount)} ✨</div>}
        {item && <p className="font-poppins mt-2">You unlocked the {item} in Addy&apos;s Shop!</p>}
        <div className="text-sm font-poppins mt-3 tracking-widest uppercase text-white/70">Added to your Achievements</div>
      </>
    );
  }
  if (card.type === 'topic-level-up') {
    const green = card.to === 'green';
    return (
      <>
        <div className="badge-burst mx-auto flex justify-center">
          {green ? <MasteredShield size={140} /> : <div className="text-8xl leading-none max-md:text-6xl">⭐</div>}
        </div>
        <div className="text-4xl font-bold font-poppins mt-2">LEVEL UP!</div>
        <div className="text-xl font-poppins mt-1">You turned this topic {green ? 'green' : 'amber'}!</div>
        {card.amount > 0 && <div className="text-lg font-poppins mt-2">+{formatSparks(card.amount)} ✨</div>}
        <div className="text-sm font-poppins mt-3 tracking-widest uppercase text-white/70">{green ? 'Topic mastered' : 'Keep climbing to green'}</div>
      </>
    );
  }
  // level
  const bracket = bracketFor(card.to, sparks.levelBrackets);
  const newBracket = bracket && bracketFor(card.from, sparks.levelBrackets)?.name !== bracket.name;
  const also = [];
  for (let l = card.from + 1; l < card.to; l += 1) also.push(l);
  const progress = levelProgress(sparks.lifetime, card.to, sparks.levelThresholds);
  return (
    <>
      <div className="badge-burst mx-auto">
        <img ref={badgeRef} src={levelBadgeUrl(card.to)} alt="" width={160} height={168} style={badgeStyle} />
      </div>
      <div className="text-4xl font-bold font-poppins mt-2">Level {card.to}!</div>
      {newBracket && <div className="text-xl font-poppins mt-1">You reached {bracket.name}!</div>}
      {also.length > 0 && <div className="font-poppins text-white/80 mt-1">You also unlocked {plural(also.length, 'level', 'levels')} {also.join(', ')}</div>}
      {progress ? (
        <div className="mt-4 text-left">
          <div className="level-bar" aria-hidden><span style={{ width: `${progress.pct}%` }} /></div>
          <div className="text-xs font-poppins mt-1 text-white/80">Earn {formatSparks(progress.toGo)} more Sparks for Level {card.to + 1}</div>
        </div>
      ) : (
        card.to >= 50 && <div className="font-poppins mt-3">You&apos;ve mastered every level!</div>
      )}
      <div className="text-sm font-poppins mt-3 tracking-widest uppercase text-white/70">Added to your Achievements</div>
    </>
  );
}
